// src/components/admin/PersonalisationModals.jsx
// Modals for the Personalisation History tab: a live preview of how a campaign looks to a
// targeted customer in the Customer Portal, and the list of customers it targets.
import React, { useEffect, useState } from 'react';
import { PortalPopupCard } from '../customer/PortalPopup';
import { HeroMedia, GalleryMedia } from '../customer/PortalMedia';
import { getCampaignRecipients } from '../../api/portalPersonalisationService';
import '../../styles/customer-preview-modal.css';

const DEVICES = [
  { id: 'phone', label: 'Phone', width: 390 },
  { id: 'tablet', label: 'Tablet', width: 720 },
  { id: 'laptop', label: 'Laptop', width: 1000 }
];

const useEscapeToClose = (onClose) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
};

export function LivePreviewModal({ campaign, salonImages, onClose }) {
  const [device, setDevice] = useState('phone');
  const frameWidth = DEVICES.find(d => d.id === device).width;
  const isPopup = campaign.type === 'popup';
  useEscapeToClose(onClose);

  // Anything the campaign doesn't set falls back to the Images page, exactly as in the portal
  const hero = campaign.heroImage || salonImages?.hero;
  const gallery = campaign.galleryImages?.length > 0 ? campaign.galleryImages : (salonImages?.gallery || []);

  return (
    <div className="modal-overlay">
      <div className="customer-preview-modal pp-live-preview">
        <div className="modal-header">
          <h3>Live Preview — {campaign.name}</h3>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body">
          <p className="form-helper-text">
            How this appears in the Customer Portal for a targeted customer.
            {!isPopup && ' The page layout is approximate; the images and videos are exactly what the customer will see. Hero videos play with sound where the browser allows it and are muted when a gallery video is played; tap a gallery video to play it with sound.'}
          </p>

          <div className="pp-device-pills">
            {DEVICES.map(d => (
              <button
                key={d.id}
                type="button"
                className={`pp-device-pill ${device === d.id ? 'active' : ''}`}
                onClick={() => setDevice(d.id)}
              >
                {d.label}
              </button>
            ))}
          </div>

          <div className="pp-device-stage">
            <div className="pp-device-frame" style={{ width: frameWidth, maxWidth: '100%' }}>
              {isPopup ? (
                <div className="pp-popup-stage">
                  <PortalPopupCard
                    imageUrl={campaign.imageUrl}
                    headerText={campaign.headerText}
                    bodyText={campaign.bodyText}
                    ctaText={campaign.ctaText}
                  />
                </div>
              ) : (
                <div className="pp-content-stage">
                  {hero ? (
                    <HeroMedia src={hero} className="pp-hero" alt="Hero" />
                  ) : (
                    <p className="form-helper-text">No hero image.</p>
                  )}
                  {gallery.length > 0 && (
                    <>
                      <h4 className="pp-gallery-title">Our Gallery</h4>
                      <div className="pp-gallery-grid">
                        {gallery.map((src, i) => (
                          <GalleryMedia key={`${src}-${i}`} src={src} alt={`Gallery ${i + 1}`} className="pp-gallery-item" />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {!isPopup && (
            <p className="form-helper-text">
              {campaign.heroImage ? 'Hero: from this campaign.' : 'Hero: not set in this campaign, so the Images page hero is used.'}{' '}
              {campaign.galleryImages?.length > 0 ? 'Gallery: from this campaign.' : 'Gallery: not set in this campaign, so the Images page gallery is used.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function RecipientsModal({ campaign, onClose }) {
  const [recipients, setRecipients] = useState(null);
  const [error, setError] = useState('');
  const [filterText, setFilterText] = useState('');
  useEscapeToClose(onClose);

  useEffect(() => {
    let cancelled = false;
    getCampaignRecipients(campaign)
      .then(list => { if (!cancelled) setRecipients(list); })
      .catch(err => {
        console.error('Error loading recipients:', err);
        if (!cancelled) setError('Failed to load recipients: ' + err.message);
      });
    return () => { cancelled = true; };
  }, [campaign]);

  const needle = filterText.toLowerCase();
  const rows = (recipients || [])
    .filter(c => !needle || c.displayName.toLowerCase().includes(needle) || c.email.toLowerCase().includes(needle))
    .sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email));

  return (
    <div className="modal-overlay">
      <div className="customer-preview-modal">
        <div className="modal-header">
          <h3>Recipients — {campaign.name}</h3>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          {!error && recipients === null && <p>Loading...</p>}

          {recipients && (
            <>
              <p className="form-helper-text">
                {campaign.audienceAll
                  ? 'This campaign targets all customers, so this is every current customer of the salon.'
                  : 'The customers selected when this campaign was created.'}
                {' '}Total: {recipients.length}.
              </p>

              {recipients.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">👥</div>
                  <p className="empty-state-text">No recipients found</p>
                </div>
              ) : (
                <>
                  <div className="table-filter">
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                    {filterText && (
                      <button type="button" className="filter-clear-btn" onClick={() => setFilterText('')}>Clear</button>
                    )}
                  </div>
                  <div className="customer-table-container">
                    <table className="customer-table">
                      <thead>
                        <tr><th>Customer Name</th><th>Email Address</th></tr>
                      </thead>
                      <tbody>
                        {rows.map(c => (
                          <tr key={c.id}>
                            <td>{c.displayName || 'N/A'}</td>
                            <td>{c.email || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
